/**
 * Filtro spam: come si comporta il filtro per ogni casella, e le liste dei
 * mittenti sicuri e bloccati.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api/client';
import { accountsApi, spamApi, type ModoSpam, type VoceLista } from '@/lib/api/endpoints';
import { cn } from '@/lib/utils';

const MODI: { id: ModoSpam; nome: string; testo: string }[] = [
  { id: 'prudent', nome: 'Prudente (consigliato)',
    testo: 'Sposta nello spam solo ciò che è evidentemente spam o una truffa, e rimette in arrivo solo la posta sicura: risposte alle tue email e mittenti fidati. Il resto lo segnala.' },
  { id: 'auto', nome: 'Automatico',
    testo: 'Come Prudente, ma si fida di più dell’AI: sposta anche lo spam meno evidente e rimette in arrivo ciò che l’AI giudica sicuro. Più comodo, un po’ meno cauto.' },
  { id: 'suggest', nome: 'Solo segnalazioni',
    testo: 'Non sposta mai niente da solo. Mette un avviso sulla posta sospetta e, nella cartella Spam, ti suggerisce cosa non è spam. Per vedere cosa farebbe prima di fidarsi.' },
  { id: 'off', nome: 'Spento', testo: 'Il filtro non fa niente: restano solo i filtri del server di posta.' },
];

export function SpamShieldCard() {
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => accountsApi.list() });
  // Solo le caselle proprie: quelle condivise le gestisce chi le possiede.
  const proprie = (accounts.data?.accounts ?? []).filter((a) => !a.shared);
  const [accountId, setAccountId] = useState<string | null>(null);
  useEffect(() => {
    if (!accountId && proprie[0]) setAccountId(proprie[0].id);
  }, [accountId, proprie]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> Filtro spam</CardTitle>
        <CardDescription>
          Tiene lo spam fuori dalla posta in arrivo e ritrova le mail vere che il server ha scartato per errore. Non cancella mai niente: ciò che sposta lo trovi nella cartella Spam.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {proprie.length > 1 && (
          <select
            value={accountId ?? ''}
            onChange={(e) => setAccountId(e.target.value)}
            className="h-9 rounded-md border bg-background px-3 text-sm"
            aria-label="Casella"
          >
            {proprie.map((a) => <option key={a.id} value={a.id}>{a.email_address}</option>)}
          </select>
        )}
        {accountId ? <SpamShieldAccount accountId={accountId} /> : <p className="text-sm text-muted-foreground">Nessuna casella collegata.</p>}
      </CardContent>
    </Card>
  );
}

function SpamShieldAccount({ accountId }: { accountId: string }) {
  const qc = useQueryClient();
  const riepilogo = useQuery({ queryKey: ['spam-summary', accountId], queryFn: () => spamApi.summary(accountId) });
  const modo = riepilogo.data?.mode;

  const cambia = useMutation({
    mutationFn: (m: ModoSpam) => spamApi.setMode(accountId, m),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['spam-summary', accountId] }); toast.success('Filtro spam aggiornato'); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Non riesco a salvare'),
  });

  return (
    <div className="space-y-6">
      <div className="grid gap-2">
        {MODI.map((m) => (
          <button
            key={m.id}
            type="button"
            disabled={cambia.isPending || !riepilogo.data}
            onClick={() => cambia.mutate(m.id)}
            className={cn(
              'text-left rounded-lg border p-3 transition-colors',
              modo === m.id ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
            )}
          >
            <div className="flex items-center gap-2">
              <span className={cn('h-3.5 w-3.5 rounded-full border-2', modo === m.id ? 'border-primary bg-primary' : 'border-muted-foreground/40')} />
              <span className="text-sm font-medium">{m.nome}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1 ml-5.5 pl-0.5">{m.testo}</p>
          </button>
        ))}
      </div>

      {riepilogo.data && (
        <p className="text-xs text-muted-foreground">
          Ultimi 7 giorni: {riepilogo.data.filtrate7g} messaggi spostati nello spam dal filtro, {riepilogo.data.rimesse7g} rimessi in arrivo.
        </p>
      )}

      <Liste accountId={accountId} />
    </div>
  );
}

function Liste({ accountId }: { accountId: string }) {
  const qc = useQueryClient();
  const liste = useQuery({ queryKey: ['spam-lists', accountId], queryFn: () => spamApi.lists(accountId) });
  const [nuovaSicura, setNuovaSicura] = useState('');
  const [nuovaBloccata, setNuovaBloccata] = useState('');

  const aggiungi = useMutation({
    mutationFn: ({ kind, valore }: { kind: 'allow' | 'block'; valore: string }) => {
      const v = valore.trim();
      // Con la chiocciola è un indirizzo, senza è un dominio intero.
      return spamApi.addToList({ accountId, kind, match: v.includes('@') && !v.startsWith('@') ? 'email' : 'domain', value: v });
    },
    onSuccess: (_, { kind }) => {
      void qc.invalidateQueries({ queryKey: ['spam-lists', accountId] });
      if (kind === 'allow') setNuovaSicura(''); else setNuovaBloccata('');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Non riesco ad aggiungerlo'),
  });
  const togli = useMutation({
    mutationFn: (id: string) => spamApi.removeFromList(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['spam-lists', accountId] }),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Non riesco a toglierlo'),
  });

  if (liste.isLoading) return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;

  const colonna = (
    titolo: string, descrizione: string, voci: VoceLista[], valore: string, setValore: (v: string) => void, kind: 'allow' | 'block'
  ) => (
    <div className="space-y-2">
      <div>
        <div className="text-sm font-medium">{titolo}</div>
        <p className="text-xs text-muted-foreground">{descrizione}</p>
      </div>
      <form
        className="flex gap-2"
        onSubmit={(e) => { e.preventDefault(); if (valore.trim()) aggiungi.mutate({ kind, valore }); }}
      >
        <Input value={valore} onChange={(e) => setValore(e.target.value)} placeholder="nome@azienda.it oppure azienda.it" className="h-8 text-sm" />
        <Button type="submit" size="sm" variant="outline" disabled={aggiungi.isPending || !valore.trim()}>
          <Plus className="h-4 w-4" />
        </Button>
      </form>
      <ul className="space-y-1">
        {voci.length === 0 && <li className="text-xs text-muted-foreground">Nessuno.</li>}
        {voci.map((v) => (
          <li key={v.id} className="flex items-center gap-2 rounded-md border px-2 py-1 text-sm">
            <span className="truncate flex-1">{v.match === 'domain' ? `@${v.value}` : v.value}</span>
            {v.source === 'feedback' && <span className="text-[10px] text-muted-foreground">da una tua scelta</span>}
            <button type="button" onClick={() => togli.mutate(v.id)} className="text-muted-foreground hover:text-foreground" title="Togli">
              <X className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="grid gap-6 md:grid-cols-2">
      {colonna('Mittenti sicuri', 'Non finiscono mai nello spam: se il server li scarta, tornano in arrivo da soli.',
        liste.data?.allow ?? [], nuovaSicura, setNuovaSicura, 'allow')}
      {colonna('Mittenti bloccati', 'Non si rimettono mai in arrivo, qualunque cosa dica l’AI.',
        liste.data?.block ?? [], nuovaBloccata, setNuovaBloccata, 'block')}
    </div>
  );
}
